/**
 * BoloDetailModal.tsx — v0.4.9.2.3c17 PWA
 *
 * A modal that displays full BOLO details with a LARGE image. Used by:
 *   1. The History view (clickable history rows)
 *   2. The Active view (when the user taps a BOLO alert card)
 *
 * The image can be tapped to go full-screen (helpful when an agent
 * needs to see a missing-person's face clearly on a small phone screen).
 *
 * v0.4.9.2.3c17: X close button rendered INVISIBLE in c16 because
 *   it relied on `bg-muted` + `text-foreground` — Tailwind theme
 *   tokens that resolve to low contrast (or undefined) in some
 *   Capacitor WebView builds. User reported "no X button, only way
 *   out is to click Home" on a deployed APK.
 *
 *   Fix: TWO close buttons, both using EXPLICIT Tailwind colors.
 *     1. Absolute top-right corner X (red bg, white X) — the main
 *        close button, sits over the modal content with z-10.
 *     2. Sticky-bottom footer button — backup, always visible
 *        regardless of scroll position, especially useful on long
 *        descriptions where the corner X might be obscured.
 *
 * v0.4.9.2.3c16: close button (X) upgraded to a prominent round
 *   button with bg-muted background and w-7 h-7 icon — ≈52px tap
 *   target on mobile (above the 44px Apple HIG minimum). Previous
 *   version had a 20x20 X with p-1 that was too easy to miss.
 *   c17 supersedes this because bg-muted wasn't visible in user's
 *   environment.
 *
 * Why a dedicated component?
 *   - The same modal works for history rows AND active alerts — single
 *     place for the styling.
 *   - Image fullscreen view is shared.
 *   - Click-outside, ESC, corner X, and footer Close all work.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, AlertOctagon, User, X, Maximize2 } from 'lucide-react';

export interface BoloDetailForModal {
  id: string;
  target_type: 'missing_person' | 'most_wanted' | 'stolen_vehicle';
  target_name: string | null;
  target_description: string | null;
  target_photo_url: string | null;
  scope: string;
  country: string;
  protocol: string;
  issued_at: string;
  cancelled_at: string | null;
  issuing_org_name: string | null;
  alert_status: 'active' | 'cancelled' | 'acknowledged';
  acknowledged_at: string | null;
  alert_created_at: string;
}

interface BoloDetailModalProps {
  bolo: BoloDetailForModal | null;
  onClose: () => void;
}

function targetTypeLabel(t: string, tt: (k: string) => string): string {
  if (t === 'most_wanted') return tt('bolo.type.shortMostWanted');
  if (t === 'stolen_vehicle') return tt('bolo.type.shortStolenVehicle');
  return tt('bolo.type.shortMissingPerson');
}

function chipColor(t: string): string {
  if (t === 'most_wanted') return 'bg-red-600 text-white';
  if (t === 'stolen_vehicle') return 'bg-blue-600 text-white';
  return 'bg-yellow-500 text-white';
}

function statusColor(s: string): string {
  if (s === 'cancelled') return 'bg-gray-400 text-white';
  if (s === 'acknowledged') return 'bg-blue-500 text-white';
  return 'bg-red-600 text-white';
}

function statusLabel(s: string, tt: (k: string) => string): string {
  if (s === 'cancelled') return tt('bolo.status.cancelled');
  if (s === 'acknowledged') return tt('bolo.status.acknowledged');
  return tt('bolo.status.activeWord');
}

export default function BoloDetailModal({ bolo, onClose }: BoloDetailModalProps) {
  const { t } = useTranslation();
  const [showFullImage, setShowFullImage] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!bolo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showFullImage) setShowFullImage(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bolo, showFullImage, onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!bolo) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [bolo]);

  if (!bolo) return null;

  const Icon = bolo.target_type === 'stolen_vehicle'
    ? Car
    : bolo.target_type === 'most_wanted'
    ? AlertOctagon
    : User;

  // Full-screen image viewer (overlay on top of the modal)
  if (showFullImage && bolo.target_photo_url) {
    return (
      <div
        className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
        onClick={() => setShowFullImage(false)}
        data-testid="bolo-fullscreen-image"
      >
        <button
          type="button"
          onClick={() => setShowFullImage(false)}
          className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full"
        aria-label={t('bolo.modal.closeImageAria')}
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={bolo.target_photo_url}
          alt={bolo.target_name || t('bolo.target.label')}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/60 px-3 py-1.5 rounded">
          {t('bolo.modal.tapToClose')}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bolo-detail-modal"
    >
      {/* v0.4.9.2.3c17: relative positioning so the absolute X
          button can sit on the top-right corner OUTSIDE the
          scroll container. The close button now lives at the
          corner overlay (absolute), not in the header flex —
          guarantees it's visible regardless of Tailwind theme tokens
          the previous code relied on (bg-muted, text-foreground). */}
      <div
        className="relative bg-card border rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* v0.4.9.2.3c17: ABSOLUTE top-right X close button.
            Uses explicit Tailwind colors (bg-red-600, text-white) so
            visibility doesn't depend on the project's custom theme
            tokens. Sits on top of the header with z-10, ~52px tap
            target, shadow for separation. Hover deepens the red;
            active state confirms the tap. */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 flex items-center justify-center w-10 h-10 rounded-full text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-lg ring-2 ring-white transition-colors focus:outline-none focus:ring-red-300"
          aria-label={t('bolo.modal.closeDetailAria')}
          data-testid="bolo-detail-modal-close"
        >
          <X className="w-6 h-6" strokeWidth={2.5} />
        </button>

        {/* Header — chips on the left, no X here anymore (it's
            absolute in the top-right). Extra right padding keeps
            chips from running under the floating close button. */}
        <div className="flex items-center gap-2 p-3 pr-14 border-b">
          <span className={`text-xs font-bold px-2 py-0.5 rounded ${chipColor(bolo.target_type)}`}>
            {targetTypeLabel(bolo.target_type, t)}
          </span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded ${statusColor(bolo.alert_status)}`}>
            {statusLabel(bolo.alert_status, t)}
          </span>
        </div>

        {/* Image — tap to view full screen */}
        <div className="p-4 bg-muted">
          {bolo.target_photo_url ? (
            <button
              type="button"
              onClick={() => setShowFullImage(true)}
              className="relative w-full block group focus:outline-none focus:ring-2 focus:ring-primary rounded"
              data-testid="bolo-detail-image-button"
            aria-label={t('bolo.modal.tapFullAria')}
            >
              <img
                src={bolo.target_photo_url}
                alt={bolo.target_name || t('bolo.target.label')}
                className="w-full h-auto max-h-96 object-contain bg-white rounded"
              />
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1 opacity-70 group-hover:opacity-100">
                <Maximize2 className="w-3 h-3" />
                {t('bolo.modal.fullScreen')}
              </div>
            </button>
          ) : (
            <div className="w-full h-48 bg-card border-2 border-dashed border-border rounded flex flex-col items-center justify-center text-muted-foreground">
              <Icon className="w-12 h-12 mb-2" />
              <span className="text-xs">{t('bolo.modal.noPhoto')}</span>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="p-4 space-y-3">
          <div>
            <h2 className="text-lg font-bold">
        {t('bolo.target.unknown')}
            </h2>
          </div>

          {bolo.target_description && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                {t('bolo.field.description')}
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">
                {bolo.target_description}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {t('records.field.country')}
              </div>
              <div className="font-mono">{bolo.country}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {t('bolo.field.scope')}
              </div>
              <div>{bolo.scope}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {t('bolo.field.protocol')}
              </div>
              <div>{bolo.protocol || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {t('bolo.field.issued')}
              </div>
              <div>{bolo.issued_at ? new Date(bolo.issued_at).toLocaleString() : '—'}</div>
            </div>
            {bolo.cancelled_at && (
              <div className="col-span-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                  {t('bolo.field.cancelled')}
                </div>
                <div>{new Date(bolo.cancelled_at).toLocaleString()}</div>
              </div>
            )}
            {bolo.acknowledged_at && (
              <div className="col-span-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                  {t('bolo.field.acknowledged')}
                </div>
                <div>{new Date(bolo.acknowledged_at).toLocaleString()}</div>
              </div>
            )}
            {bolo.issuing_org_name && (
              <div className="col-span-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {t('bolo.field.issuedBy')}
                </div>
                <div>{bolo.issuing_org_name}</div>
              </div>
            )}
          </div>

          <div className="pt-2 border-t text-xs text-muted-foreground font-mono break-all">
            {t('bolo.field.id')} {bolo.id}
          </div>
        </div>

        {/* v0.4.9.2.3c17: Footer — explicit "Close" button as a
            backup. Even with the visible top-right corner X, having
            a clearly-labelled Close button at the bottom of the
            modal content gives users a second obvious dismissal path.
            Especially helpful on long BOLO descriptions where the
            corner X is offscreen before the user scrolls back. */}
        <div className="sticky bottom-0 bg-card border-t p-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-lg border border-gray-300 text-gray-800 bg-white hover:bg-gray-100 active:bg-gray-200 font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
            data-testid="bolo-detail-modal-close-footer"
          >
            <X className="w-4 h-4" />
            {t('bolo.modal.closeBack')}
          </button>
        </div>
      </div>
    </div>
  );
}
